import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { CORPORATE_STRUCTURE_OPTIONS, FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AvatarImageUploadField,
  Field,
  Form,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useFileUpload } from 'src/hooks/file';
import { useCurrentUserPeo } from 'src/hooks/peo';
import { validateWithYupSchema } from 'src/utils/yup';

import * as styles from './styles';
import {
  CompanyInformationFormPeoAdminFields,
  CompanyInformationFormPeoAdminValues,
} from './types';
import { getInitialValues } from './utils/getInitialValues';
import { companyInformationFormPeoAdminValidationSchema } from './utils/validation';

export const CompanyInformationFormPeoAdmin: React.FC = () => {
  const { peo, isPartnership, updatePeo } = useCurrentUserPeo();
  const { uploadFile } = useFileUpload();

  const initialValues = React.useMemo(() => (peo ? getInitialValues(peo) : {}), [peo]);

  const handleSubmit = React.useCallback(
    async ({
      displayName,
      contactEmail,
      contactPhoneNumber,
      industry,
      corporateStructure,
      taxYearEndDate,
      numberOfEmployees,
      headquarters,
      defaultDisclaimer,
      fein,
      logoFile,
    }: CompanyInformationFormPeoAdminValues) => {
      try {
        let newLogoId: string | undefined;
        if (logoFile?.file && logoFile.id !== peo?.logo?.id) {
          const newLogo = await uploadFile(logoFile.file);
          newLogoId = newLogo.id;
        }

        await updatePeo({
          displayName,
          contactEmail: contactEmail?.toLowerCase(),
          contactPhoneNumber: contactPhoneNumber ?? null,
          industry: industry ?? null,
          corporateStructure: corporateStructure ?? null,
          taxYearEndDate: taxYearEndDate ?? null,
          fein,
          numberOfEmployees,
          defaultDisclaimer: defaultDisclaimer || '',
          headquarters: {
            [!peo?.headquarters?.id ? 'create' : 'update']: {
              apartment: headquarters?.apartment ?? null,
              city: headquarters?.city ?? null,
              county: headquarters?.county ?? null,
              state: headquarters?.state ?? null,
              street: headquarters?.street ?? null,
              zipCode: headquarters?.zipCode ?? null,
            },
          },
          logo: newLogoId ? { connect: { id: newLogoId } } : undefined,
        });
      } catch (error) {
        console.error(error);
      }
    },
    [peo?.headquarters?.id, peo?.logo?.id, updatePeo, uploadFile],
  );

  return (
    <Form<CompanyInformationFormPeoAdminValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={validateWithYupSchema(companyInformationFormPeoAdminValidationSchema)}
    >
      {({ submitting, pristine, form: { reset } }) => (
        <Grid container spacing={3}>
          <Grid size={6}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Field
                name={CompanyInformationFormPeoAdminFields.displayName}
                label="Company Display Name"
                required
                fullWidth
                disabled={submitting}
                component={TextField}
              />
              <Field
                name={CompanyInformationFormPeoAdminFields.taxYearEndDate}
                label="Tax Year End Date"
                fullWidth
                disabled={submitting}
                component={MaskedTextField}
                inputProps={{
                  placeholder: 'MM/DD',
                }}
                mask={TAX_YEAR_END_DATE_MASK}
              />
            </Box>
          </Grid>
          <Grid size={4}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Field
                component={MaskedTextField}
                name={CompanyInformationFormPeoAdminFields.fein}
                label="FEIN"
                mask={FEIN_MASK}
                fullWidth
                disabled={submitting}
                required
              />

              <Field
                name={CompanyInformationFormPeoAdminFields.numberOfEmployees}
                label="Number of Employees"
                fullWidth
                disabled={submitting}
                component={NumberField}
              />
            </Box>
          </Grid>
          <Grid display="flex" alignItems="center" justifyContent="center" size={2}>
            <Field
              name={CompanyInformationFormPeoAdminFields.logoFile}
              component={AvatarImageUploadField}
              defaultImageUrl={peo?.logo?.downloadUrl || DEFAULT_AVATAR}
              avatarProps={{ sx: styles.avatarStyles }}
              disabled={submitting}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoAdminFields.corporateStructure}
              label="Corporate Structure"
              fullWidth
              disabled={submitting}
              options={CORPORATE_STRUCTURE_OPTIONS}
              component={SelectField}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoAdminFields.industry}
              label="Industry"
              fullWidth
              disabled={submitting}
              options={INDUSTRIES_OPTIONS}
              component={SelectField}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoAdminFields.contactPhoneNumber}
              label="Primary Contact Phone"
              fullWidth
              disabled={submitting}
              component={PhoneField}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoAdminFields.contactEmail}
              label="Primary Contact Email"
              required
              fullWidth
              disabled={submitting}
              component={TextField}
            />
          </Grid>

          <Grid spacing={2} size={12}>
            <Grid size={12}>
              <Typography fontWeight={600}>Headquarters address</Typography>
            </Grid>
            <Grid mt={2}>
              <AddressFields
                prefix={CompanyInformationFormPeoAdminFields.headquarters}
                requiredStreet
                requiredCity
                requiredState
                requiredZipCode
                disabled={submitting}
                addressLabel="Street"
              />
            </Grid>
          </Grid>

          <React.Activity mode={isPartnership ? 'hidden' : 'visible'}>
            <Grid size={12}>
              <DefaultDisclaimerInputSection
                headerText="PEO Disclaimer"
                name={CompanyInformationFormPeoAdminFields.defaultDisclaimer}
              />
            </Grid>
          </React.Activity>

          <Grid size={12}>
            <Box display="flex" gap={3} justifyContent="center">
              <Button
                disabled={pristine || submitting}
                variant="outlined"
                onClick={() => reset()}
                color="error"
              >
                Reset
              </Button>
              <Button
                disabled={pristine || submitting}
                loading={submitting}
                variant="contained"
                type="submit"
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
